import React, { useContext, useEffect, useState } from 'react';
import axiosInstance from '../../../configurations/axiosInstance/AxiosInstance';
import './readArticles.css';
import { useParams } from 'react-router-dom';
import ViewArticle from '../../viewArticle';
import { LoadingContext } from '../../../composants/loadingContext';
import Loader from '../../../composants/loader/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNewspaper, faCalendar, faUser, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { NavLink } from 'react-router-dom';

const ReadArticles = () => {
    const {loading, setLoading} = useContext(LoadingContext);
    const {id} = useParams();
    const [read, setRead] = useState(null);
    
    const fetchArticle = async ()=>{
        setLoading(true);
        try {
            const response = await axiosInstance().get('public/api/article/get/'+ id);
            let data = response.data;
            if(data){
                setRead(data);
                document.title = data.title || 'Article';
            } else {
                setRead(null);
            }
        } catch (error) {
            console.error('Error fetching article:', error);
            setRead(null);
        }
        setLoading(false);
    }

    useEffect(
        ()=>{
            fetchArticle();
        },
        [id]
    )

    return (
        <>
            {loading && <Loader />}
            
            {read === null && !loading ? (
                <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center py-20">
                    <div className="text-center max-w-md mx-auto px-4">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full mb-6">
                            <FontAwesomeIcon icon={faNewspaper} className="text-5xl text-purple-500" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-3">
                            Article non trouvé
                        </h3>
                        <p className="text-gray-600 mb-6">
                            L'article que vous recherchez n'existe pas ou a été supprimé.
                        </p>
                        <NavLink 
                            to="/"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-medium hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                            Retour à l'accueil
                        </NavLink>
                    </div>
                </div>
            ) : read && (
                <>
                    {/* Hero Section with Article Image */}
                    {read.imagePrincipal && (
                        <div className="relative h-96 overflow-hidden bg-gradient-to-br from-purple-600 via-pink-500 to-blue-500">
                            <div className="absolute inset-0">
                                <img 
                                    src={read.imagePrincipal}
                                    alt={read.title}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                            </div>
                            
                            <div className="relative h-full flex items-end">
                                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
                                    <NavLink 
                                        to="/"
                                        className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors duration-300"
                                    >
                                        <FontAwesomeIcon icon={faArrowLeft} />
                                        <span className="font-medium">Retour</span>
                                    </NavLink>
                                    
                                    <div className="flex flex-wrap gap-3 mb-4">
                                        {read.nouveau && (
                                            <span className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium">
                                                Nouveau
                                            </span>
                                        )}
                                        {read.categorie && (
                                            <span className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full text-sm font-medium">
                                                {read.categorie}
                                            </span>
                                        )}
                                    </div>
                                    
                                    <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
                                        {read.title}
                                    </h1>
                                    
                                    <div className="flex flex-wrap items-center gap-6 text-white/80">
                                        {read.auteur && (
                                            <div className="flex items-center gap-2">
                                                <FontAwesomeIcon icon={faUser} />
                                                <span>{read.auteur}</span>
                                            </div>
                                        )}
                                        {read.dateCreation && (
                                            <div className="flex items-center gap-2">
                                                <FontAwesomeIcon icon={faCalendar} />
                                                <span>{new Date(read.dateCreation).toLocaleDateString('fr-FR', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Article Content */}
                    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-2">
                        <div className="w-[95%] mx-auto px-4 sm:px-6 lg:px-8">
                            {!read.imagePrincipal && (
                                <div className="mb-8">
                                    <NavLink 
                                        to="/"
                                        className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6 transition-colors duration-300"
                                    >
                                        <FontAwesomeIcon icon={faArrowLeft} />
                                        <span className="font-medium">Retour</span>
                                    </NavLink>
                                    
                                    <div className="flex flex-wrap gap-3 mb-4">
                                        {read.nouveau && (
                                            <span className="px-4 py-2 bg-gradient-to-r from-green-100 to-teal-100 text-green-700 rounded-full text-sm font-medium">
                                                Nouveau
                                            </span>
                                        )}
                                        {read.categorie && (
                                            <span className="px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full text-sm font-medium">
                                                {read.categorie}
                                            </span>
                                        )}
                                    </div>
                                    
                                    <h1 className="text-4xl md:text-5xl font-extrabold text-gray-800 mb-4 tracking-tight">
                                        {read.title}
                                    </h1>
                                    
                                    <div className="flex flex-wrap items-center gap-6 text-gray-600">
                                        {read.auteur && (
                                            <div className="flex items-center gap-2">
                                                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                                                    <FontAwesomeIcon icon={faUser} className="text-white text-sm" />
                                                </div>
                                                <span className="font-medium">{read.auteur}</span>
                                            </div>
                                        )}
                                        {read.dateCreation && (
                                            <div className="flex items-center gap-2">
                                                <FontAwesomeIcon icon={faCalendar} className="text-purple-500" />
                                                <span>{new Date(read.dateCreation).toLocaleDateString('fr-FR', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}</span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="h-1 w-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mt-6"></div>
                                </div>
                            )}
                            
                            <div className="bg-white rounded-2xl shadow-lg p-1 md:p-2">
                                <ViewArticle content={read}/>
                            </div>
                            
                            {/* Share Section */}
                            <div className="mt-8 text-center">
                                <NavLink 
                                    to="/"
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-medium hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                    Retour aux articles
                                </NavLink>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default ReadArticles;